import { yupResolver } from '@hookform/resolvers/yup';
import { useGdprConsents } from 'connectors/gdpr/gdprConsents';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { useSocialLoginDataContext } from 'context/SocialLoginDataProvider/SocialLoginDataProvider';
import { GdprActionTypeEnumApi } from 'graphql/generated';
import Yup from 'helpers/forms/yup-extended';
import { getDefaultGdprConsents } from 'helpers/gdprConsents/getDefaultGdprConsents';
import { B2C_IT_DOMAIN_ID, isDomain, isDomainLocale } from 'helpers/isDomainLocale';
import { useCustomerUserAgeValidationConstants } from 'hooks/forms/useCustomerUserAgeValidationConstants';
import { useShopsysForm } from 'hooks/forms/UseShopsysForm';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { FieldError, UseFormReturn, useWatch } from 'react-hook-form';
import { SelectOptionType } from 'types/selectOptions';

export type RegistrationFormType = {
    email: string;
    passwordFirst: string;
    passwordSecond: string;
    companyCustomer: boolean;
    telephone: string;
    birthDay: Date | null;
    firstName: string;
    lastName: string;
    street: string;
    houseNumber: string;
    district: SelectOptionType;
    city: string;
    postcode: string;
    country: SelectOptionType;
    dayOfBirth: string;
    monthOfBirth: string;
    yearOfBirth: string;
    companyName: string;
    companyNumber: string;
    companyTaxNumber: string;
    companyTaxIdNumber: string;
    gdprConsents: string[];
};

export const useRegistrationForm = (): [UseFormReturn<RegistrationFormType>, RegistrationFormType] => {
    const gdprConsents = useGdprConsents(GdprActionTypeEnumApi.RegisterApi, null);
    const { t } = useTranslation();
    const { maximalDate, minimalAge } = useCustomerUserAgeValidationConstants();
    const { defaultLocale, domainId } = useDomainConfig();
    const isItalianB2c = isDomain(domainId, B2C_IT_DOMAIN_ID);
    const currentYear = new Date().toLocaleDateString(defaultLocale, { year: 'numeric' });
    const minimumYearOfBirth = parseInt(currentYear) - 100;
    const maximumYearOfBirth = parseInt(currentYear) - 14;
    const isPortugalLocale = isDomainLocale(defaultLocale, 'pt');
    const socialLoginData = useSocialLoginDataContext();

    const resolver = yupResolver(
        Yup.object().shape({
            email: Yup.string().required(t('Please enter email')).email(t('This value is not a valid email')).min(5),
            passwordFirst: Yup.string()
                .required(t('Please enter password'))
                .min(
                    6,
                    t('Password must be at least {{ count }} characters long', {
                        count: 6,
                    }),
                ),
            passwordSecond: Yup.string()
                .required(t('Please enter password'))
                .min(
                    6,
                    t('Password must be at least {{ count }} characters long', {
                        count: 6,
                    }),
                )
                .oneOf([Yup.ref('passwordFirst'), null], t('Passwords must match')),
            companyCustomer: Yup.boolean(),
            telephone: Yup.string()
                .trim()
                .required(t('Please enter phone number'))
                .matches(
                    /^\+?[0-9]*$/,
                    t('Please enter only numbers; the + character is allowed only at the beginning'),
                )
                .test(
                    'more-than-8',
                    t('Telephone number cannot be shorter than 9 characters'),
                    (value) => value !== undefined && value.length >= 9,
                )
                .max(
                    30,
                    t('Telephone number cannot be longer than {{ limit }} characters', {
                        limit: 30,
                    }),
                ),
            firstName: Yup.string()
                .trim()
                .required(t('Please enter first name'))
                .max(
                    100,
                    t('First name cannot be longer than {{ limit }} characters', {
                        limit: 100,
                    }),
                )
                .when([], {
                    is: () => isItalianB2c,
                    then: (schema) => schema.matches(/^[\p{L}'’.\- ]+$/u, t('Please enter a valid first name')),
                }),
            lastName: Yup.string()
                .trim()
                .required(t('Please enter last name'))
                .max(
                    100,
                    t('Last name cannot be longer than {{ limit }} characters', {
                        limit: 100,
                    }),
                )
                .when([], {
                    is: () => isItalianB2c,
                    then: (schema) => schema.matches(/^[\p{L}'’.\- ]+$/u, t('Please enter a valid last name')),
                }),
            street: Yup.string()
                .required(t('Please enter street'))
                .matches(/\p{L}+/u, t('The street must contain a letter'))
                .max(
                    100,
                    t('Street name cannot be longer than {{ limit }} characters', {
                        limit: 100,
                    }),
                ),
            houseNumber: isItalianB2c
                ? Yup.string()
                      .trim()
                      .required(t('Please enter a valid house number'))
                      .matches(/^[0-9A-Za-z/\- ]{1,15}$/, {
                          message: t('Please enter a valid house number'),
                          excludeEmptyString: true,
                      })
                : Yup.string(),
            district: isItalianB2c
                ? Yup.object()
                      .shape({ label: Yup.string().required(), value: Yup.string().required() })
                      .required(t('Please choose district'))
                : Yup.object().shape({ label: Yup.string(), value: Yup.string() }),
            city: Yup.string()
                .required(t('Please enter city'))
                .max(
                    100,
                    t('City name cannot be longer than {{ limit }} characters', {
                        limit: 100,
                    }),
                ),
            postcode: isItalianB2c
                ? Yup.string()
                      .trim()
                      .required(t('Please enter zip code'))
                      .matches(/^\d{5}$/, t('Postal code must contain exactly 5 digits'))
                : Yup.string().required(t('Please enter zip code')).validatePostCode(defaultLocale, t, null),
            country: Yup.object()
                .shape({
                    label: Yup.string().required(),
                    value: Yup.string().required(),
                })
                .required(t('Please enter country'))
                .test(
                    'non-null-or-empty-string',
                    t('Please enter country'),
                    (value: { label: string; value: string }) => value.value !== '',
                ),
            dayOfBirth: Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'day-of-birth',
                    t('Please enter a valid value'),
                    (value) =>
                        value !== undefined && (value.length === 0 || (parseInt(value) >= 1 && parseInt(value) <= 31)),
                ),
            monthOfBirth: Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'month-of-birth',
                    t('Please enter a valid value'),
                    (value) =>
                        value !== undefined && (value.length === 0 || (parseInt(value) >= 1 && parseInt(value) <= 12)),
                ),
            yearOfBirth: Yup.string()
                .matches(/^[0-9]*$/, t('Please enter only numbers'))
                .test(
                    'year-of-birth',
                    t('Please enter a valid value'),
                    (value) =>
                        value !== undefined &&
                        (value.length === 0 ||
                            (parseInt(value) > minimumYearOfBirth && parseInt(value) <= maximumYearOfBirth)),
                ),
            birthDay: Yup.date().validateCustomerBirthDay(t, maximalDate, minimalAge),
            companyName: Yup.string().when('companyCustomer', {
                is: true,
                then: Yup.string()
                    .required(t('Please enter company name'))
                    .max(
                        100,
                        t('Company name cannot be longer than {{ limit }} characters', {
                            limit: 100,
                        }),
                    ),
                otherwise: Yup.string(),
            }),
            companyNumber: Yup.string().when('companyCustomer', {
                is: true,
                then: isPortugalLocale ? Yup.string() : Yup.string().validateCompanyNumber(defaultLocale, t),
                otherwise: isPortugalLocale ? Yup.string().validateCompanyNumber(defaultLocale, t) : Yup.string(),
            }),
            companyTaxNumber: Yup.string().when('companyCustomer', {
                is: true,
                then: Yup.string().validateCompanyTaxNumber(defaultLocale, t),
                otherwise: Yup.string(),
            }),
            companyTaxIdNumber: Yup.string().when('companyCustomer', {
                is: true,
                then: Yup.string().validateCompanyTaxIdNumber(defaultLocale, t),
                otherwise: Yup.string(),
            }),
            gdprConsents: Yup.array().test('all-granted', t('All required consents need to be granted.'), (it) =>
                (gdprConsents ?? [])
                    .filter((item) => item.isRequired)
                    .map((item) => item.type)
                    .every((arr2Item) => it?.includes(arr2Item)),
            ),
        }),
    );

    const defaultValues: RegistrationFormType = {
        email: socialLoginData?.email ?? '',
        passwordFirst: '',
        passwordSecond: '',
        companyCustomer: false,
        telephone: socialLoginData?.telephone ?? '',
        birthDay: null,
        firstName: socialLoginData?.firstName ?? '',
        lastName: socialLoginData?.lastName ?? '',
        street: socialLoginData?.street ?? '',
        houseNumber: '',
        district: { value: '', label: '' },
        city: socialLoginData?.city ?? '',
        postcode: socialLoginData?.postcode ?? '',
        country: socialLoginData?.country
            ? { value: socialLoginData.country, label: socialLoginData.country }
            : { value: '', label: '' },
        dayOfBirth: '',
        monthOfBirth: '',
        yearOfBirth: '',
        companyName: '',
        companyNumber: '',
        companyTaxNumber: '',
        companyTaxIdNumber: '',
        gdprConsents: getDefaultGdprConsents(gdprConsents),
    };

    return [useShopsysForm(resolver, defaultValues), defaultValues];
};

type RegistrationFormMetaType = {
    formName: string;
    messages: {
        error: string;
        success: string;
        successAndLogged: string;
        completedAndLogged: string;
        successWithoutConfirmation: string;
    };
    fields: {
        [key in keyof RegistrationFormType]: {
            name: key;
            label: string | JSX.Element;
            errorMessage: string | undefined;
        };
    };
    getFieldId(fieldName: string): string;
};

export const useRegistrationFormMeta = (
    formProviderMethods: UseFormReturn<RegistrationFormType>,
): RegistrationFormMetaType => {
    const { t } = useTranslation();
    const domain = useDomainConfig();

    const companyCustomerFieldName = 'companyCustomer' as const;

    const [companyCustomerValue] = useWatch({
        name: [companyCustomerFieldName],
        control: formProviderMethods.control,
    });

    const errors = formProviderMethods.formState.errors;

    const formMeta = useMemo(
        () => ({
            formName: 'registration-form',
            messages: {
                error: t('Could not create account'),
                success: t('Your account has been created'),
                successAndLogged: t('Your account has been created and you are logged in now'),
                completedAndLogged: t('Your registration has been completed and you are logged in now'),
                successWithoutConfirmation: t(
                    'Your account has been created, please finish your registration in email',
                ),
            },
            fields: {
                email: {
                    name: 'email' as const,
                    label: t('Your email'),
                    errorMessage: errors.email?.message,
                },
                passwordFirst: {
                    name: 'passwordFirst' as const,
                    label: t('Password'),
                    errorMessage: errors.passwordFirst?.message,
                },
                passwordSecond: {
                    name: 'passwordSecond' as const,
                    label: t('Password again'),
                    errorMessage: errors.passwordSecond?.message,
                },
                [companyCustomerFieldName]: {
                    name: companyCustomerFieldName,
                    label: t('Add company details'),
                    errorMessage: errors.companyCustomer?.message,
                },
                telephone: {
                    name: 'telephone' as const,
                    label: t('Phone'),
                    errorMessage: errors.telephone?.message,
                },
                birthDay: {
                    name: 'birthDay' as const,
                    label: t('Date of birth'),
                    errorMessage: errors.birthDay?.message,
                },
                firstName: {
                    name: 'firstName' as const,
                    label: t('First name'),
                    errorMessage: errors.firstName?.message,
                },
                lastName: {
                    name: 'lastName' as const,
                    label: t('Last name'),
                    errorMessage: errors.lastName?.message,
                },
                companyName: {
                    name: 'companyName' as const,
                    label: t('Company name'),
                    errorMessage: companyCustomerValue === true ? errors.companyName?.message : undefined,
                },
                companyNumber: {
                    name: 'companyNumber' as const,
                    label: t('Company number'),
                    errorMessage: companyCustomerValue === true ? errors.companyNumber?.message : undefined,
                },
                companyTaxNumber: {
                    name: 'companyTaxNumber' as const,
                    label: t('Tax number'),
                    errorMessage: companyCustomerValue === true ? errors.companyTaxNumber?.message : undefined,
                },
                companyTaxIdNumber: {
                    name: 'companyTaxIdNumber' as const,
                    label: t('Tax Id number'),
                    errorMessage:
                        companyCustomerValue === true && domain.useTaxId
                            ? errors.companyTaxIdNumber?.message
                            : undefined,
                },
                street: {
                    name: 'street' as const,
                    label: isDomain(domain.domainId, B2C_IT_DOMAIN_ID) ? t('Address') : t('Street and house no.'),
                    errorMessage: errors.street?.message,
                },
                houseNumber: {
                    name: 'houseNumber' as const,
                    label: t('House number'),
                    errorMessage: errors.houseNumber?.message,
                },
                district: {
                    name: 'district' as const,
                    label: t('Provincia'),
                    errorMessage: formProviderMethods.formState.errors.district?.message,
                },
                city: {
                    name: 'city' as const,
                    label: t('City'),
                    errorMessage: errors.city?.message,
                },
                postcode: {
                    name: 'postcode' as const,
                    label: t('Postcode'),
                    errorMessage: errors.postcode?.message,
                },
                country: {
                    name: 'country' as const,
                    label: t('Country'),
                    errorMessage: (errors.country as FieldError | undefined)?.message,
                },
                dayOfBirth: {
                    name: 'dayOfBirth' as const,
                    label: t('DayOfBirth'),
                    errorMessage: errors.dayOfBirth?.message,
                },
                monthOfBirth: {
                    name: 'monthOfBirth' as const,
                    label: t('MonthOfBirth'),
                    errorMessage: errors.monthOfBirth?.message,
                },
                yearOfBirth: {
                    name: 'yearOfBirth' as const,
                    label: t('YearOfBirth'),
                    errorMessage: errors.yearOfBirth?.message,
                },
                gdprConsents: {
                    name: 'gdprConsents' as const,
                    label: t('Consent to GDPR'),
                    errorMessage: (errors.gdprConsents as FieldError | undefined)?.message,
                },
            },
            getFieldId(fieldName: string) {
                return `${formMeta.formName}-${fieldName}`;
            },
        }),
        [
            t,
            errors.email?.message,
            errors.passwordFirst?.message,
            errors.passwordSecond?.message,
            errors.companyCustomer?.message,
            errors.telephone?.message,
            errors.birthDay?.message,
            errors.firstName?.message,
            errors.lastName?.message,
            errors.companyName?.message,
            errors.companyNumber?.message,
            errors.companyTaxNumber?.message,
            errors.companyTaxIdNumber?.message,
            errors.street?.message,
            errors.city?.message,
            errors.postcode?.message,
            errors.country,
            errors.dayOfBirth?.message,
            errors.monthOfBirth?.message,
            errors.yearOfBirth?.message,
            errors.gdprConsents,
            companyCustomerValue,
            domain.useTaxId,
        ],
    );

    return formMeta;
};
